import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useTranslate, useDataProvider } from "ra-core";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import type { CrmDataProvider } from "../providers/types";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_INPUT_ID,
  SEARCH_MAX_RESULTS,
  SEARCH_MIN_CHARS,
  SEARCH_TYPES,
  searchHitPath,
  type GlobalSearchHit,
  type SearchHitType,
} from "./searchConfig";

const GROUP_KEYS: Record<SearchHitType, string> = {
  contact: "crm.search.groups.contact",
  company: "crm.search.groups.company",
  note: "crm.search.groups.note",
};

const useDebounced = (value: string, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const useSearchHotkey = (onInvoke: () => void) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onInvoke();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onInvoke]);
};

const SearchResults = ({
  query,
  variant,
  listId,
  onNavigate,
  onActiveId,
  onClose,
  bindKeys,
}: {
  query: string;
  variant: "popover" | "inline";
  listId: string;
  onNavigate?: () => void;
  onActiveId: (id: string | undefined) => void;
  onClose: () => void;
  bindKeys: (
    handler: (event: ReactKeyboardEvent<HTMLInputElement>) => void,
  ) => void;
}) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [active, setActive] = useState(0);
  const { data, isError, isFetched, isFetching } = useQuery({
    queryKey: ["globalSearch", query],
    queryFn: () => dataProvider.globalSearch(query, SEARCH_MAX_RESULTS),
    retry: false,
  });
  const groups = useMemo(() => {
    const hits = data ?? [];
    return SEARCH_TYPES.map((type) => ({
      type,
      label: translate(GROUP_KEYS[type]),
      hits: hits.filter((hit) => hit.type === type),
    })).filter((group) => group.hits.length > 0);
  }, [data, translate]);
  const flat = useMemo(() => groups.flatMap((group) => group.hits), [groups]);
  const showEmpty = isFetched && !isFetching && !isError && flat.length === 0;

  useEffect(() => {
    setActive(0);
  }, [query]);

  const openHit = useCallback(
    (hit: GlobalSearchHit) => {
      onClose();
      onNavigate?.();
      navigate(searchHitPath(hit));
    },
    [navigate, onClose, onNavigate],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (flat.length === 0) {
        if (event.key === "Escape") onClose();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActive((index) => Math.min(index + 1, flat.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActive((index) => Math.max(index - 1, 0));
      } else if (event.key === "Enter") {
        event.preventDefault();
        const hit = flat[active] ?? flat[0];
        if (hit) openHit(hit);
      } else if (event.key === "Escape") {
        onClose();
      }
    },
    [active, flat, onClose, openHit],
  );

  useEffect(() => {
    bindKeys(onKeyDown);
  }, [bindKeys, onKeyDown]);

  useEffect(() => {
    onActiveId(flat[active] ? `${listId}-opt-${active}` : undefined);
  }, [active, flat, listId, onActiveId]);

  let optionIndex = -1;
  return (
    <div
      id={listId}
      role="listbox"
      aria-label={translate("crm.search.label")}
      className={cn(
        "z-40 max-h-[70vh] overflow-y-auto rounded-lg border bg-popover text-popover-foreground shadow-md",
        variant === "popover"
          ? "absolute left-full top-0 ml-2 w-80"
          : "mt-3 w-full",
      )}
    >
      {isError ? (
        <p role="alert" className="px-3 py-3 text-sm">
          {translate("crm.search.error")}
        </p>
      ) : null}
      {showEmpty ? (
        <p role="status" className="px-3 py-3 text-sm text-muted-foreground">
          {translate("crm.search.empty")}
        </p>
      ) : null}
      {groups.map((group) => (
        <div key={group.type}>
          <p className="px-3 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {group.label}
          </p>
          {group.hits.map((hit) => {
            optionIndex += 1;
            const index = optionIndex;
            const selected = index === active;
            return (
              <button
                key={`${hit.type}-${hit.id}`}
                id={`${listId}-opt-${index}`}
                type="button"
                role="option"
                aria-selected={selected}
                className={cn(
                  "block w-full px-3 py-2 text-left",
                  selected ? "bg-accent" : "hover:bg-accent",
                )}
                onMouseDown={(event) => {
                  event.preventDefault();
                  openHit(hit);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <span className="block text-sm font-medium">{hit.title}</span>
                {hit.subtitle ? (
                  <span className="block truncate text-xs text-muted-foreground">
                    {hit.subtitle}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const SearchField = ({
  variant,
  onNavigate,
}: {
  variant: "popover" | "inline";
  onNavigate?: () => void;
}) => {
  const translate = useTranslate();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [activeId, setActiveId] = useState<string>();
  const debounced = useDebounced(query, SEARCH_DEBOUNCE_MS);
  const enabled = debounced.trim().length >= SEARCH_MIN_CHARS;
  const showResults = enabled && (variant === "inline" || focused);
  const onKeys = useRef<(event: ReactKeyboardEvent<HTMLInputElement>) => void>(
    () => undefined,
  );
  const bindKeys = useCallback(
    (handler: (event: ReactKeyboardEvent<HTMLInputElement>) => void) => {
      onKeys.current = handler;
    },
    [],
  );
  const onActiveId = useCallback((id: string | undefined) => {
    setActiveId(id);
  }, []);
  const onClose = useCallback(() => {
    setQuery("");
    setFocused(false);
  }, []);

  return (
    <div
      className={cn(
        "relative",
        variant === "popover" ? "w-[6.5rem]" : "w-full",
      )}
    >
      <label className="relative block">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-1.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <input
          id={SEARCH_INPUT_ID}
          type="search"
          role="searchbox"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          value={query}
          placeholder={translate(
            variant === "inline"
              ? "crm.search.placeholder_long"
              : "crm.search.placeholder",
          )}
          aria-label={translate("crm.search.label")}
          aria-expanded={showResults}
          aria-controls={showResults ? listId : undefined}
          aria-activedescendant={showResults ? activeId : undefined}
          className={cn(
            "h-8 w-full rounded-md border border-input bg-background pl-6 pr-1 text-xs outline-none",
            "placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring",
          )}
          onChange={(event) => {
            setQuery(event.target.value);
            setFocused(true);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(event) => {
            if (!showResults) {
              if (event.key === "Escape") setQuery("");
              return;
            }
            onKeys.current(event);
          }}
        />
      </label>
      {showResults ? (
        <SearchResults
          query={debounced}
          variant={variant}
          listId={listId}
          onNavigate={onNavigate}
          onActiveId={onActiveId}
          onClose={onClose}
          bindKeys={bindKeys}
        />
      ) : null}
    </div>
  );
};

export const DesktopGlobalSearch = () => {
  const focus = useCallback(() => {
    document.getElementById(SEARCH_INPUT_ID)?.focus();
  }, []);
  useSearchHotkey(focus);
  return <SearchField variant="popover" />;
};

export const MobileGlobalSearch = () => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const openSearch = useCallback(() => setOpen(true), []);
  useSearchHotkey(openSearch);

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={translate("crm.search.open")}
        onClick={openSearch}
      >
        <Search />
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="top"
          className="h-dvh w-full max-w-none gap-3 overflow-y-auto border-0"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            document.getElementById(SEARCH_INPUT_ID)?.focus();
          }}
        >
          <SheetHeader>
            <SheetTitle>{translate("crm.search.label")}</SheetTitle>
            <SheetDescription className="sr-only">
              {translate("crm.search.placeholder_long")}
            </SheetDescription>
          </SheetHeader>
          <SearchField variant="inline" onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
};
